import { useState, useEffect } from "react";
import { IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import TitleIcon from "@mui/icons-material/Title";
import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import AddIcon from "@mui/icons-material/Add";
import CropIcon from "@mui/icons-material/Crop";
import DescriptionIcon from "@mui/icons-material/Description";

interface SendFilePreviewProps {
  file: File;
  onClose: () => void;
  onSend: (file: File, caption: string) => void;
}

const SendFilePreview = ({ file, onClose, onSend }: SendFilePreviewProps) => {
  const [caption, setCaption] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    const isMedia = file.type.startsWith("image/") || file.type.startsWith("video/");
    if (isMedia) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [file]);

  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");

  return (
    <div className="absolute inset-0 z-[100] bg-[#e9edef] flex flex-col sm:rounded-b-2xl md:rounded-b-2xl overflow-hidden h-full w-full">
      {/* Top Header */}
      <div className="flex items-center justify-between p-3 shrink-0">
        <div className="flex items-center gap-2">
          <Tooltip title="Cancel">
            <IconButton onClick={onClose} sx={{ color: "#54656f" }}>
              <CloseIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2 text-[#54656f]">
          {/* {isImage && (
            <>
              <Tooltip title="Crop">
                <IconButton sx={{ color: "inherit" }}><CropIcon /></IconButton>
              </Tooltip>
              <Tooltip title="Emoji">
                <IconButton sx={{ color: "inherit" }}><AddReactionOutlinedIcon /></IconButton>
              </Tooltip>
              <Tooltip title="Text">
                <IconButton sx={{ color: "inherit" }}><TitleIcon /></IconButton>
              </Tooltip>
            </>
          )} */}
        </div>
      </div>

      {/* Preview Area */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-4">
        {previewUrl ? (
          isVideo ? (
            <video src={previewUrl} controls className="max-h-full max-w-full object-contain rounded-md shadow-lg" />
          ) : (
            <img src={previewUrl} alt="Preview" className="max-h-full max-w-full object-contain rounded-md shadow-lg" />
          )
        ) : (
          <div className="flex flex-col items-center justify-center bg-white w-48 h-48 rounded-xl shadow-md border border-gray-100">
             <DescriptionIcon sx={{ fontSize: 64, color: "#94a3b8" }} className="mb-4" />
             <p className="text-sm font-medium text-gray-600 truncate px-4 w-full text-center">{file.name}</p>
             <p className="text-xs text-gray-400 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        )}
      </div>

      {/* Caption Input Area */}
      <div className="bg-[#f0f2f5] p-3 flex flex-col gap-3 shrink-0">
         <div className="flex items-center gap-3 w-full bg-white rounded-xl px-4 py-3 shadow-sm mx-auto max-w-2xl">
          <input
            type="text"
            className="flex-1 bg-transparent border-none outline-none text-[15px] text-gray-800 placeholder:text-gray-400"
            placeholder="Type a message"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSend(file, caption);
              }
            }}
            autoFocus
          />
        </div>
      </div>

      {/* Bottom Footer Area */}
      <div className="bg-[#e9edef] h-16 flex items-center justify-between px-4 shrink-0 relative">
        <div className="flex items-center gap-2">
           <div className="h-12 w-12 border-2 border-blue-500 rounded-md overflow-hidden relative opacity-100 bg-white flex items-center justify-center">
             {previewUrl ? (
                isVideo ? (
                  <video src={previewUrl} className="w-full h-full object-cover" />
                ) : (
                  <img src={previewUrl} className="w-full h-full object-cover" />
                )
             ) : (
                <DescriptionIcon sx={{ fontSize: 24, color: "#94a3b8" }} />
             )}
           </div>
           {/* Add More Files Button Placeholder */}
           {/* <div className="h-12 w-12 border border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-100 text-gray-500">
             <AddIcon />
           </div> */}
        </div>

        {/* Send Button */}
        <div className="absolute right-4 -top-8">
            <IconButton
              onClick={() => onSend(file, caption)}
              sx={{
                width: 54,
                height: 54,
                backgroundColor: "blue",
                color: "#ffffff",
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "#00008B",
                },
              }}
            >
              <SendIcon sx={{ ml: 0.5, fontSize: 24  }} />
            </IconButton>
        </div>
      </div>
    </div>
  );
};

export default SendFilePreview;
